/* eslint-disable quotes */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, useRef, useCallback } from 'react'
import { CSVLink } from 'react-csv'
import { Grid, Typography, Button } from '@material-ui/core'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useLocation, useParams, NavLink } from 'react-router-dom'
import PersonOutlineIcon from '@material-ui/icons/PersonOutline'
import { Helmet } from 'react-helmet'
import GetAppIcon from '@material-ui/icons/GetApp'
import CircularProgress from '@material-ui/core/CircularProgress'
import CommonSnackbar from '../CommonSnackbar/CommonSnackbar'
import {
  useResultGetQuery,
  useAllResultGetQuery,
  useResultIdGetQueryMutate,
} from '../../Queries/ResultQuery'
import Loader from '../Loader/Loader'
import {
  modifyQuizIntervalFormat,
  modifyDateTimeFormat,
  unixTimeStamp,
  htmlToString,
} from '../../utils'
import QuizNotFound from '../QuizNotFound/QuizNotFound'
import { useStyles } from './DisplayResultsStyles'
import ResultsPagination from './ResultsPagination'
import { useQuizQuestionGetQueryById } from '../../Queries/QuizQuery'

const safeDate = (v) => {
  if (!v) return null
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

const safeFormatDateTime = (v) => {
  const d = safeDate(v)
  return d ? d.toLocaleString() : ''
}

const safeUnixTimeStamp = (v) => {
  if (!v && v !== 0) return null
  try {
    return unixTimeStamp(v)
  } catch {
    return null
  }
}

const normalizeTimestamp = (v) => {
  if (!v) return null

  const num = Number(v)

  if (num < 1e12) {
    return num * 1000
  }

  // Already milliseconds
  return num
}

export default function DisplayResults() {
  const { id } = useParams()
  const [finalHeaders, setFinalHeaders] = useState([])
  const [candidateResultArray, setCandidateResultArray] = useState([])
  const [allResults, setAllResults] = useState([])
  const [totalResultsCount, updateTotalResultsCount] = useState(0)
  const [resultsPageOffset, setResultPageOffset] = useState(0)
  const [resultPagesArray, setResultPagesArray] = useState([])
  const [resultPageLimit] = useState(10)
  const [userResults, setUserResults] = useState([])
  const [message, setMessage] = useState('')
  const [truthValue, setTruthValue] = useState(false)

  const [quizDetails, setQuizDetails] = useState({
    quizTitle: '',
    quizStartTime: '',
    quizEndTime: '',
    quizType: '',
    quizId: '',
    isProctored: '',
  })

  const resultsCSVRef = useRef(null)
  const submissionsCSVRef = useRef(null)
  const aboveTabScreenSize = useMediaQuery('(min-width:960px)')
  const classes = useStyles({ aboveTabScreenSize })
  const location = useLocation()
  const [inviteeCount, setInviteeCount] = useState(0)
  const [quizNotFound, setQuizNotFound] = useState(false)
  const [selectedButton, setSelectedButton] = useState('')

  /** Safely seed quizDetails from router state or localStorage */
  useEffect(() => {
    if (location?.state) {
      const {
        quizTitle,
        quizStartTime,
        quizEndTime,
        quizType,
        quizId,
        isProctored,
        isClosed,
      } = location.state
      setQuizDetails((prev) => ({
        ...prev,
        quizTitle: quizTitle || '',
        quizStartTime: quizStartTime || '',
        quizEndTime: quizEndTime || '',
        quizType: quizType || '',
        quizId: quizId || '',
        isProctored: isProctored ?? '',
        isClosed: isClosed ?? '',
      }))
      return
    }

    try {
      const raw = localStorage.getItem('quizList')
      const jsonParsedQuizList = raw ? JSON.parse(raw) : null
      const filteredQuiz = Array.isArray(jsonParsedQuizList)
        ? jsonParsedQuizList.find((q) => `${q?.id}` === id)
        : null

      if (filteredQuiz) {
        setQuizDetails((prev) => ({
          ...prev,
          quizTitle: filteredQuiz?.info?.title || '',
          quizStartTime: filteredQuiz?.time?.start || '',
          quizEndTime: filteredQuiz?.time?.end || '',
          quizType: filteredQuiz?.quizConfig?.type || '',
          quizId: filteredQuiz?.id || '',
          isProctored: filteredQuiz?.quizConfig?.isProctored ?? '',
          isClosed: filteredQuiz?.isClosed ?? '',
        }))
      }
    } catch {
      // invalid JSON in localStorage, nothing to do
    }
  }, [location, id])

  const headers = [
    { label: 'Name', key: 'name' },
    { label: 'Email ID', key: 'emailID' },
    { label: 'Start time', key: 'startTime' },
    { label: 'End time', key: 'endTime' },
    { label: 'Submission time', key: 'submissionTime' },
    { label: 'Marks', key: 'marks' },
    { label: 'Tab Change Count', key: 'tabChangeCount' },
  ]

  const resultOne = useResultIdGetQueryMutate(id, {
    enabled: true,
    retry: false,
    staleTime: 0,
  })

  const {
    data: resultsData,
    isSuccess: isCandidateListSuccess,
    isLoading: isCandidateListLoading,
    isFetching: isCandidateListFetching,
  } = useResultGetQuery(id, resultPageLimit, resultsPageOffset, {
    stale: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    onSuccess: (successResponse) => {
      const list = Array.isArray(successResponse?.data?.data)
        ? successResponse.data.data
        : []
      const meta = successResponse?.data?.metadata || {}
      const total = Number(meta?.total) || 0
      updateTotalResultsCount(total)
      if (total > 0) {
        setResultPagesArray(
          new Array(Math.ceil(total / resultPageLimit)).fill(0)
        )
      } else {
        setResultPagesArray([])
      }
      setCandidateResultArray(list)
      setInviteeCount(Number(meta?.inviteeCount) || 0)
    },
    onError: (err) => {
      if (
        (err.status === 404 && err.code === 'ENTITY_NOT_FOUND') ||
        (err.status === 400 && err.code === 'BAD_REQUEST')
      ) {
        setQuizNotFound(true)
      }
    },
  })

  /** Safer duration calc (no throws, tolerates bad inputs) */
  const calculateSubmissionTime = (startTime, submissionTime) => {
    if (!startTime) return 'Not Started'
    if (!submissionTime) return 'Not Submitted'

    const start = safeDate(normalizeTimestamp(startTime))
    const end = safeDate(normalizeTimestamp(submissionTime))
    if (!start || !end) return 'Not Submitted'

    let diffMs = end.getTime() - start.getTime()
    if (diffMs < 0) diffMs = 0

    const totalSecs = Math.floor(diffMs / 1000)
    const hours = Math.floor(totalSecs / 3600)
    const minutes = Math.floor((totalSecs % 3600) / 60)
    const seconds = totalSecs % 60

    let out = ''
    if (hours) out += `${hours} Hrs `
    if (minutes) out += `${minutes} Mins `
    if (seconds || !out) out += `${seconds} Secs `
    return out.trim()
  }

  const { refetch: refetchQuizQuestionById } = useQuizQuestionGetQueryById(id, {
    staleTime: 0,
    enabled: false,
    retry: false,
    onSuccess: (successData = {}) => {
      const questionTypes = [
        { key: 'mcq', defaultType: 'MCQ', prefix: 'mcq' },
        { key: 'coding', defaultType: 'Coding', prefix: 'coding' },
        { key: 'essay', defaultType: 'Essay', prefix: 'essay' },
        { key: 'fillInBlanks', defaultType: 'Fill', prefix: 'fillInBlanks' },
      ]

      const qh = questionTypes.flatMap(({ key, defaultType, prefix }) =>
        (successData[key] || []).flatMap((question, index) => [
          {
            label: `${htmlToString(question?.question || '')} (${
              question?.type || defaultType
            })`,
            key: `${prefix}Question_${index}`,
          },
          { label: 'score', key: `${prefix}Score_${index}` },
        ])
      )

      setFinalHeaders([...headers, ...qh])
    },
  })

  useEffect(() => {
    refetchQuizQuestionById()
  }, [refetchQuizQuestionById])

  const replaceDoubleQuotes = (str) =>
    typeof str === 'string' ? str.replace(/"/g, "'") : str

  const convertArrayToObject = (userIdentifier) => {
    const userSubmissions = (userResults || []).find(
      (csvData) => csvData?.userID === userIdentifier
    )
    const { mcq, coding, essay, fillInBlanks } =
      userSubmissions?.evaluatedResult ?? {}
    const submissions = {}

    ;(mcq || []).forEach((question, index) => {
      submissions[`mcqQuestion_${index}`] =
        question?.result?.selectedChoice ?? ''
      submissions[`mcqScore_${index}`] = question?.result?.marks ?? ''
    })
    ;(coding || []).forEach((question, index) => {
      submissions[`codingQuestion_${index}`] = replaceDoubleQuotes(
        question?.code ?? ''
      )
      submissions[`codingScore_${index}`] = question?.result?.marks ?? ''
    })
    ;(essay || []).forEach((question, index) => {
      submissions[`essayQuestion_${index}`] =
        question?.result?.userResponse ?? ''
      submissions[`essayScore_${index}`] = question?.result?.marks ?? ''
    })
    ;(fillInBlanks || []).forEach((question, index) => {
      submissions[`fillInBlanksQuestion_${index}`] =
        question?.result?.userResponse ?? ''
      submissions[`fillInBlanksScore_${index}`] = question?.result?.marks ?? ''
    })

    return submissions
  }

  const getCandidateRow = (candidateResultInfo) => {
    const c = candidateResultInfo || {}
    const startUnix = safeUnixTimeStamp(c?.activeUserTime?.startTime)
    const submissionUnix = safeUnixTimeStamp(c?.activeUserTime?.submissionTime)
    const isPublic = (quizDetails?.quizType || '').toLowerCase() === 'public'

    return {
      name: c?.username ?? '',
      emailID: isPublic ? 'N/A' : c?.userID ?? '',
      startTime: startUnix ? modifyDateTimeFormat(startUnix) : '',
      endTime: c?.activeUserTime?.submissionTime
        ? submissionUnix
          ? modifyDateTimeFormat(submissionUnix)
          : ''
        : 'Not Submitted',
      submissionTime:
        c?.activeUserTime?.submissionTime == null
          ? 'Not Submitted'
          : calculateSubmissionTime(
              c?.activeUserTime?.startTime,
              c?.activeUserTime?.submissionTime
            ),
      marks: c?.evaluatedMarks ?? '',
      tabChangeCount: c?.tabChangeCount ?? 0,
    }
  }

  const getSubmissionsCsvData = () =>
    (allResults || []).map((candidateResultInfo) => ({
      ...getCandidateRow(candidateResultInfo),
      ...convertArrayToObject(candidateResultInfo?.userID),
    }))

  const getResultsCsvData = () => (allResults || []).map(getCandidateRow)

  useEffect(() => {
    if ((userResults || []).length > 0) {
      const copyDataCsv = getSubmissionsCsvData()
      if (copyDataCsv.length > 0 && submissionsCSVRef?.current?.link) {
        submissionsCSVRef.current.link.click()
        setTruthValue(true)
        setMessage('File Downloaded Succesfully !!')
      }
    }
  }, [userResults])

  const handleAllData = async (emailsData) => {
    try {
      const resultPromise = await Promise.allSettled(
        (emailsData || []).map(async (userIdentifier) =>
          resultOne.mutateAsync(userIdentifier)
        )
      )
      const userObject = resultPromise
        .filter((p) => p.status === 'fulfilled')
        .map((p) => p?.value?.data?.data)
        .filter(Boolean)
      if (userObject.length === 0) {
        throw new Error('Something unexpected happened !!')
      }
      setUserResults(userObject)
    } catch (error) {
      setMessage(error?.message || 'Something went wrong !!')
    }
  }

  const {
    isLoading: isAllResultsLoading,
    isSuccess: isAllResultsSuccess,
    isFetching: isAllResultsFetching,
    refetch: allResultsRefetch,
    data: allResultsData,
  } = useAllResultGetQuery(id, {
    stale: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: false,
    onSuccess: (successResponse) => {
      const responses = successResponse?.data?.data
      setAllResults(Array.isArray(responses) ? responses : [])
      if (selectedButton === 'submissions') {
        const emails = (responses || []).map((r) => r?.userID).filter(Boolean)
        handleAllData(emails)
      }
    },
    onError: () => {
      setMessage('something went wrong !!')
    },
  })

  const handleDownloads = useCallback(async () => {
    await allResultsRefetch()
  }, [allResultsRefetch])

  const handlePrev = () => {
    setResultPageOffset((prevOffset) =>
      prevOffset - resultPageLimit >= 0 ? prevOffset - resultPageLimit : 0
    )
  }

  const handleNext = () => {
    setResultPageOffset((prevOffset) =>
      prevOffset + resultPageLimit <= totalResultsCount
        ? prevOffset + resultPageLimit
        : totalResultsCount
    )
  }

  useEffect(() => {
    if (
      selectedButton === 'results' &&
      isAllResultsSuccess &&
      Array.isArray(allResultsData?.data?.data) &&
      allResultsData.data.data.length > 0
    ) {
      const copyDataCsv = getResultsCsvData()
      if (copyDataCsv.length > 0 && resultsCSVRef?.current?.link) {
        resultsCSVRef.current.link.click()
        setTruthValue(true)
        setMessage('File Downloaded Succesfully !!')
      }
    }
  }, [isAllResultsSuccess, allResultsData, selectedButton])

  if (quizNotFound) {
    return <QuizNotFound reason="Quiz not found" />
  }

  const handleTotalAttempteeMessage = () => {
    if (inviteeCount) {
      return `Total ${totalResultsCount}/${inviteeCount} candidates attended the quiz`
    }
    return `Total ${totalResultsCount || 0} candidates attended the quiz`
  }

  const safeQuizInterval = () => {
    const start = quizDetails?.quizStartTime
    const end = quizDetails?.quizEndTime
    if (!start || !end) return ''
    try {
      return modifyQuizIntervalFormat(start, end)
    } catch {
      return `${safeFormatDateTime(start)}${start ? ' ' : ''}${
        end ? '— ' : ''
      }${safeFormatDateTime(end)}`
    }
  }

  const isDownloading =
    resultOne?.isLoading ||
    isAllResultsFetching ||
    isAllResultsLoading ||
    isCandidateListFetching ||
    isCandidateListLoading

  return (
    <div className={classes.resultListContainer}>
      <Helmet>
        <title>Result List</title>
      </Helmet>
      <CommonSnackbar
        open={message !== ''}
        autoHideDuration={6000}
        onClose={() => {
          setMessage('')
          setTimeout(() => setTruthValue(false), 100)
        }}
        severity={truthValue ? 'success' : 'error'}
        message={message}
      />

      <div className={classes.title}>
        <p>
          <span className={classes.quizResultsTitle}>
            {quizDetails?.quizTitle || ''} Results
          </span>
        </p>
      </div>

      <div className={classes.resultListBody}>
        <div className={classes.quizDetail}>
          <div className={classes.quizDurationWrapper}>
            <p className={classes.quizDuration}>{safeQuizInterval()}</p>
          </div>

          <div className={classes.downloadBtnWrapper}>
            <div className={classes.ctaWrapper}>
              {Number(resultsData?.data?.metadata?.total) > 0 ? (
                <Button
                  className={classes.downloadResultBtn}
                  onClick={() => {
                    setSelectedButton('results')
                    handleDownloads()
                  }}
                  startIcon={
                    isDownloading && selectedButton === 'results' ? null : (
                      <GetAppIcon />
                    )
                  }
                  data-testid="downloadResult-testId"
                  disabled={isDownloading}
                >
                  {isDownloading && selectedButton === 'results' ? (
                    <CircularProgress size="1.5rem" />
                  ) : (
                    'Download Results'
                  )}
                </Button>
              ) : null}
              <CSVLink
                ref={resultsCSVRef}
                data={getResultsCsvData()}
                headers={headers}
                filename={`${quizDetails?.quizTitle || 'Quiz'}-Results.csv`}
                className={classes.csvLink}
              />
            </div>

            <div className={classes.ctaWrapper}>
              {Number(resultsData?.data?.metadata?.total) > 0 ? (
                <Button
                  className={classes.downloadSubmissionBtn}
                  onClick={() => {
                    setSelectedButton('submissions')
                    handleDownloads()
                  }}
                  startIcon={
                    isDownloading && selectedButton === 'submissions' ? null : (
                      <GetAppIcon />
                    )
                  }
                  data-testid="downloadResult-testId"
                  disabled={isDownloading}
                >
                  {isDownloading && selectedButton === 'submissions' ? (
                    <CircularProgress size="1.5rem" />
                  ) : (
                    ' Download Submissions'
                  )}
                </Button>
              ) : null}
              <CSVLink
                ref={submissionsCSVRef}
                data={getSubmissionsCsvData()}
                headers={finalHeaders}
                filename={`${quizDetails?.quizTitle || 'Quiz'}-Submissions.csv`}
                className={classes.csvLink}
              />
            </div>
          </div>
        </div>

        <div className={classes.candidateCountWrapper}>
          <div className={classes.candidatesCount}>
            <div className={classes.styledPersonIcon}>
              <PersonOutlineIcon className={classes.personIcon} />
            </div>
            <span>{handleTotalAttempteeMessage()}</span>
          </div>
        </div>

        {(isCandidateListLoading || isCandidateListFetching) && (
          <div className={classes.resultListLoader}>
            <Loader size="6rem" />
          </div>
        )}

        {isCandidateListSuccess &&
        Number(resultsData?.data?.metadata?.total) > 0 ? (
          <Grid container>
            {aboveTabScreenSize && (candidateResultArray || []).length > 0 && (
              <Grid item container>
                <Grid item md={3}>
                  Name
                </Grid>
                <Grid item md={3}>
                  Submission Time
                </Grid>
                <Grid item md={2}>
                  Marks
                </Grid>
                <Grid item md={2}>
                  Tab Change
                </Grid>
                <div className={classes.resultRowHeader}>Results</div>
              </Grid>
            )}

            {(candidateResultArray || []).map((candidate) => {
              const uid =
                candidate?.userID || Math.random().toString(36).slice(2)
              const marks = candidate?.evaluatedMarks
              return (
                <Grid
                  key={uid}
                  item
                  container
                  alignItems="center"
                  className={classes.candidateCard}
                >
                  <Grid item xs={12} md={3} container alignItems="center">
                    <div className={classes.name}>
                      <Grid className={classes.styledPersonIcon}>
                        <PersonOutlineIcon className={classes.personIcon} />
                      </Grid>
                      <Typography className={classes.candidateName}>
                        {candidate?.username || '—'}
                      </Typography>
                    </div>
                  </Grid>

                  <Grid item xs={6} md={3} container direction="column">
                    {!aboveTabScreenSize && (
                      <Typography className={classes.boldText}>
                        Submission Time
                      </Typography>
                    )}
                    {candidate?.activeUserTime?.submissionTime == null
                      ? 'Not Submitted'
                      : calculateSubmissionTime(
                          candidate?.activeUserTime?.startTime,
                          candidate?.activeUserTime?.submissionTime
                        )}
                  </Grid>

                  <Grid
                    item
                    xs={3}
                    md={2}
                    container
                    direction="column"
                    style={{ color: marks >= 0 ? '' : 'red' }}
                  >
                    {!aboveTabScreenSize && (
                      <Typography
                        className={classes.boldText}
                        style={{ color: marks ? '' : 'black' }}
                      >
                        Marks
                      </Typography>
                    )}
                    {marks >= 0 ? marks : 'Evaluating'}
                  </Grid>

                  <Grid item xs={3} md={1}>
                    {!aboveTabScreenSize && (
                      <Typography className={classes.boldText}>
                        Tab Count
                      </Typography>
                    )}
                    {candidate?.tabChangeCount ?? 0}
                  </Grid>

                  <Grid
                    item
                    container
                    xs={12}
                    md={3}
                    justifyContent={!aboveTabScreenSize ? 'flex-end' : 'center'}
                    className={classes.resultBtnContainer}
                  >
                    <NavLink
                      to={`/quiz/${id}/response/${candidate?.userID}`}
                      className={classes.link}
                    >
                      <Button className={classes.resultBtn}>Results</Button>
                    </NavLink>
                  </Grid>
                </Grid>
              )
            })}

            <div className={classes.resultsPaginationDiv}>
              <ResultsPagination
                className={classes.pagination}
                totalResultsCount={totalResultsCount}
                handleNext={handleNext}
                handlePrevious={handlePrev}
                resultsPageOffset={resultsPageOffset}
                resultPagesArray={resultPagesArray}
                resultPageLimit={resultPageLimit}
                setResultPageOffset={setResultPageOffset}
              />
            </div>
          </Grid>
        ) : (
          (!isCandidateListLoading || !isCandidateListFetching) && (
            <div>
              <Typography className={classes.resultListsMsg}>
                No results found
              </Typography>
            </div>
          )
        )}
      </div>
    </div>
  )
}
